/* @flow */

import React from 'react';
import ReactDOM, { findDOMNode } from 'react-dom';

const KEYCODES = {
  ESCAPE: 27,
};

export default class Portal extends React.PureComponent {
  props: {
    render: () => React.Element,
    openByClickOn: ?React.Element,
    closeOnEsc: ?boolean,
    closeOnOutsideClick: ?boolean,
    isOpened: ?boolean,
    onOpen: () => void,
    onClose: () => void,
    beforeClose: () => void,
    onUpdate: () => void,
  };

  static defaultProps = {
    onOpen: () => {},
    onClose: () => {},
    onUpdate: () => {},
  };

  constructor() {
    super();
    this.state = { active: false };
    this.portal = null;
    this.node = null;
  }

  componentDidMount() {
    if (this.props.closeOnEsc) {
      document.addEventListener('keydown', this.handleKeydown);
    }

    if (this.props.closeOnOutsideClick) {
      document.addEventListener('mouseup', this.handleOutsideMouseClick);
      document.addEventListener('touchstart', this.handleOutsideMouseClick);
    }

    if (this.props.isOpened) {
      this.openPortal(this.props);
    }
  }

  componentWillReceiveProps(newProps) {
    // portal's 'is open' state is handled through the prop isOpened
    if (typeof newProps.isOpened !== 'undefined') {
      if (newProps.isOpened) {
        if (this.state.active) {
          this.renderPortal(newProps);
        } else {
          this.openPortal(newProps);
        }
      }
      if (!newProps.isOpened && this.state.active) {
        this.closePortal();
      }
    }

    // portal handles its own 'is open' state
    if (typeof newProps.isOpened === 'undefined' && this.state.active) {
      this.renderPortal(newProps);
    }
  }

  componentWillUnmount() {
    if (this.props.closeOnEsc) {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    if (this.props.closeOnOutsideClick) {
      document.removeEventListener('mouseup', this.handleOutsideMouseClick);
      document.removeEventListener('touchstart', this.handleOutsideMouseClick);
    }

    this.closePortal(true);
  }

  handleWrapperClick = event => {
    event.preventDefault();
    event.stopPropagation();
    if (this.state.active) { return; }
    this.openPortal();
  };

  openPortal = props => {
    this.setState({ active: true });
    this.renderPortal(props);
    this.props.onOpen(this.node);
  };

  closePortal = (isUnmounted = false) => {
    const resetPortalState = () => {
      if (this.node) {
        ReactDOM.unmountComponentAtNode(this.node);
        document.body.removeChild(this.node);
      }
      this.portal = null;
      this.node = null;
      if (isUnmounted !== true) {
        this.setState({ active: false });
      }
    };

    if (this.state.active) {
      if (this.props.beforeClose) {
        this.props.beforeClose(this.node, resetPortalState);
      } else {
        resetPortalState();
      }

      this.props.onClose();
    }
  };

  handleOutsideMouseClick = event => {
    if (!this.state.active) { return; }

    const root = findDOMNode(this.portal);
    if (root.contains(event.target) || (event.button && event.button !== 0)) { return; }

    event.stopPropagation();
    this.closePortal();
  };

  handleKeydown = event => {
    if (event.keyCode === KEYCODES.ESCAPE && this.state.active) {
      this.closePortal();
    }
  };

  renderPortal = props => {
    if (!this.node) {
      this.node = document.createElement('div');
      document.body.appendChild(this.node);
    }

    let renderFn = props.render;

    this.portal = ReactDOM.unstable_renderSubtreeIntoContainer(
      this,
      React.cloneElement(renderFn(), { closePortal: this.closePortal }),
      this.node,
      this.props.onUpdate
    );
  };

  render() {
    if (this.props.openByClickOn) {
      return React.cloneElement(this.props.openByClickOn, { onClick: this.handleWrapperClick });
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ../src/client/components/modal/Portal.js